// Global Instructions Rule Applied!
//
// Configuration → Contact Relationships services: the types → titles hierarchy
// on cfg.contact_relationship_types / cfg.contact_relationship_titles. Same
// fetch conventions as pipelineStatusesConfigService; every route is
// admin-gated at the backend (403 surfaces readably from the body). An in-use
// delete returns 409 with usage detail — the panel routes that into the
// Replace modal (and additionally pre-checks the loaded row's usage count
// client-side, the Industries pattern, so the modal opens without a round
// trip).
import { API_ROUTES, buildFullUrl, getBackendBaseUrl } from '../../../core/utils/endpoints.ts'
import {
  ContactRelationshipTypeAdminRow,
  ContactRelationshipTitleAdminRow,
  validateContactRelationshipTypeAdminRow,
  validateContactRelationshipTitleAdminRow
} from '../../../state_manager/data_models/contact_relationships_config_model.ts'

const BACKEND_BASE_URL = getBackendBaseUrl()

function authHeaders(accessToken: string | null): Record<string, string> {
  const headers: Record<string, string> = { 'Content-Type': 'application/json' }
  if (accessToken) headers['Authorization'] = `Bearer ${accessToken}`
  return headers
}

export class TitleInUseError extends Error {
  usageCount: number
  constructor(message: string, usageCount: number) {
    super(message)
    this.name = 'TitleInUseError'
    this.usageCount = usageCount
  }
}

async function readJson(response: Response): Promise<any> {
  const body = await response.json().catch(() => ({}))
  if (!response.ok) {
    if (response.status === 401) throw new Error('Authentication failed - please log in again')
    throw new Error(body.message || body.error || `Request failed (${response.status})`)
  }
  return body
}

export const fetchContactRelationshipTypes = async (
  accessToken: string | null
): Promise<ContactRelationshipTypeAdminRow[]> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.CONTACT_RELATIONSHIP_TYPES_LIST),
    {
      method: API_ROUTES.CONFIG.CONTACT_RELATIONSHIP_TYPES_LIST.method,
      headers: authHeaders(accessToken)
    }
  )
  const body = await readJson(response)
  const rows = (body.data || []) as ContactRelationshipTypeAdminRow[]
  validateContactRelationshipTypeAdminRow(rows, { isArray: true })
  return rows
}

export const createContactRelationshipType = async (
  fields: {
    name: string
    gets_comms?: boolean
    track_interactions?: boolean
    sort_order?: number | null
  },
  accessToken: string | null
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.CONTACT_RELATIONSHIP_TYPE_CREATE),
    {
      method: API_ROUTES.CONFIG.CONTACT_RELATIONSHIP_TYPE_CREATE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: fields })
    }
  )
  await readJson(response)
}

export const updateContactRelationshipType = async (
  typeId: string,
  fields: {
    name?: string
    gets_comms?: boolean
    track_interactions?: boolean
    sort_order?: number | null
  },
  accessToken: string | null
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.CONTACT_RELATIONSHIP_TYPE_UPDATE, { typeId }),
    {
      method: API_ROUTES.CONFIG.CONTACT_RELATIONSHIP_TYPE_UPDATE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: fields })
    }
  )
  await readJson(response)
}

export const deleteContactRelationshipType = async (
  typeId: string,
  accessToken: string | null
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.CONTACT_RELATIONSHIP_TYPE_DELETE, { typeId }),
    {
      method: API_ROUTES.CONFIG.CONTACT_RELATIONSHIP_TYPE_DELETE.method,
      headers: authHeaders(accessToken)
    }
  )
  await readJson(response)
}

export const fetchContactRelationshipTitles = async (
  typeId: string,
  accessToken: string | null
): Promise<ContactRelationshipTitleAdminRow[]> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.CONTACT_RELATIONSHIP_TITLES_LIST, { typeId }),
    {
      method: API_ROUTES.CONFIG.CONTACT_RELATIONSHIP_TITLES_LIST.method,
      headers: authHeaders(accessToken)
    }
  )
  const body = await readJson(response)
  const rows = (body.data || []) as ContactRelationshipTitleAdminRow[]
  validateContactRelationshipTitleAdminRow(rows, { isArray: true })
  return rows
}

export const createContactRelationshipTitle = async (
  fields: { name: string; type_id: string; sort_order?: number | null },
  accessToken: string | null
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.CONTACT_RELATIONSHIP_TITLE_CREATE),
    {
      method: API_ROUTES.CONFIG.CONTACT_RELATIONSHIP_TITLE_CREATE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: fields })
    }
  )
  await readJson(response)
}

export const updateContactRelationshipTitle = async (
  titleId: string,
  fields: { name?: string; sort_order?: number | null },
  accessToken: string | null
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.CONTACT_RELATIONSHIP_TITLE_UPDATE, { titleId }),
    {
      method: API_ROUTES.CONFIG.CONTACT_RELATIONSHIP_TITLE_UPDATE.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({ data: fields })
    }
  )
  await readJson(response)
}

export const deleteContactRelationshipTitle = async (
  titleId: string,
  accessToken: string | null
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.CONTACT_RELATIONSHIP_TITLE_DELETE, { titleId }),
    {
      method: API_ROUTES.CONFIG.CONTACT_RELATIONSHIP_TITLE_DELETE.method,
      headers: authHeaders(accessToken)
    }
  )
  if (response.status === 409) {
    const body = await response.json().catch(() => ({}))
    throw new TitleInUseError(
      body.message || body.error || 'This title is in use',
      Number(body.usage_count || 0)
    )
  }
  await readJson(response)
}

export const reassignContactRelationshipTitles = async (
  sourceIds: string[],
  targetId: string,
  deleteSources: boolean,
  accessToken: string | null
): Promise<void> => {
  const response = await fetch(
    buildFullUrl(BACKEND_BASE_URL, API_ROUTES.CONFIG.CONTACT_RELATIONSHIP_TITLES_REASSIGN),
    {
      method: API_ROUTES.CONFIG.CONTACT_RELATIONSHIP_TITLES_REASSIGN.method,
      headers: authHeaders(accessToken),
      body: JSON.stringify({
        data: { source_ids: sourceIds, target_id: targetId, delete_sources: deleteSources }
      })
    }
  )
  await readJson(response)
}
